<div class="orders-container">
  <!-- Page Header -->
  <div class="page-header">
    <div class="header-content">
      <h2 class="page-title">
        <i class="fa fa-clipboard-list"></i>
        Order Management
      </h2>

      <div class="header-actions">
        <!-- Refresh Button -->
        <button class="refresh-btn"
                (click)="refreshData()"
                [disabled]="refreshing"
                [class.refreshing]="refreshing">
          <i class="fa fa-sync-alt" [class.spinning]="refreshing"></i>
          <span class="refresh-text">{{ refreshing ? 'Refreshing...' : 'Refresh' }}</span>
        </button>

        <!-- Create Order Button -->
        <button class="btn btn-primary create-order-btn" (click)="createOrder(content)">
          <i class="fa fa-plus"></i>
          <span>Create Order</span>
        </button>
      </div>
    </div>
  </div>

  <!-- Auto-refresh indicator -->
  <div class="auto-refresh-indicator" *ngIf="refreshing && !loading">
    <div class="refresh-bar"></div>
    <small class="refresh-message">
      <i class="fa fa-sync-alt spinning"></i>
      Refreshing orders...
    </small>
  </div>

  <!-- Main Loading State -->
  <div class="main-loading" *ngIf="loading">
    <div class="loader-wrapper">
      <div class="order-loader">
        <div class="loader-circle"></div>
        <div class="loader-circle"></div>
        <div class="loader-circle"></div>
      </div>
      <p class="loading-text">Loading orders...</p>
    </div>
  </div>

  <!-- Orders Content -->
  <div class="orders-content" [class.loading]="loading" *ngIf="!loading">
    <div class="orders-card">
      <ul ngbNav #nav="ngbNav" class="modern-nav nav-pills nav-fill">
        <!-- Incoming orders tab -->
        <li ngbNavItem>
          <button class="nav-button" ngbNavLink (click)="resetNewOrderNotification()">
            <div class="nav-content">
              <i class="fa fa-inbox nav-icon"></i>
              <span class="nav-label">Incoming Orders</span>
              <span class="badge badge-danger" *ngIf="hasNewOrders">
                {{newOrderCount}}
              </span>
            </div>
          </button>
          <ng-template ngbNavContent>
            <!-- Tab Loading State -->
            <div class="tab-loading" *ngIf="unclaimedLoading">
              <div class="tab-loader">
                <div class="loader-bar"></div>
                <div class="loader-bar"></div>
                <div class="loader-bar"></div>
              </div>
              <p>Loading incoming orders...</p>
            </div>

            <!-- Orders List -->
            <div class="orders-list" *ngIf="!unclaimedLoading">
              <div class="order-card" *ngFor="let item of unclaimedOrders; trackBy: trackByOrderId">
                <div class="order-header">
                  <div class="order-info">
                    <h6 class="order-title">
                      <i class="fa fa-utensils"></i>
                      Order from {{ item.barcode_details?.name }}
                    </h6>
                    <span class="order-time">{{ getTimeElapsed(item.created_at) }}</span>
                  </div>
                  <button class="btn btn-accept" (click)="AcceptItemModal(item, content4)">
                    <i class="fa fa-check"></i>
                    Accept Order
                  </button>
                </div>
              </div>

              <!-- Empty State -->
              <div class="empty-state" *ngIf="unclaimedOrders?.length === 0">
                <div class="empty-content">
                  <i class="fa fa-inbox empty-icon"></i>
                  <h4>No Incoming Orders</h4>
                  <p>All orders have been claimed or there are no new orders at the moment.</p>
                </div>
              </div>
            </div>
          </ng-template>
        </li>

        <!-- Active orders tab -->
        <li ngbNavItem>
          <button class="nav-button" ngbNavLink>
            <div class="nav-content">
              <i class="fa fa-clock nav-icon"></i>
              <span class="nav-label">Active Orders</span>
              <span class="badge badge-primary" *ngIf="chatNotifications.length > 0">
                {{chatNotifications.length}}
              </span>
            </div>
          </button>
          <ng-template ngbNavContent>
            <!-- Tab Loading State -->
            <div class="tab-loading" *ngIf="activeLoading">
              <div class="tab-loader">
                <div class="loader-bar"></div>
                <div class="loader-bar"></div>
                <div class="loader-bar"></div>
              </div>
              <p>Loading active orders...</p>
            </div>

            <!-- Orders List -->
            <div class="orders-list" *ngIf="!activeLoading">
              <div class="order-card" *ngFor="let item of activeOrders; trackBy: trackByOrderId">
                <a [routerLink]="['/orders/', item.uid]" class="order-link">
                  <div class="order-header">
                    <div class="order-info">
                      <h6 class="order-title">
                        <i class="fa fa-utensils"></i>
                        Order from {{ item.barcode_details?.name }}
                      </h6>
                      <div class="order-badges">
                        <span class="badge badge-chat" *ngIf="hasChatNotification(item.uid)">
                          <i class="fa fa-message"></i> New Message
                        </span>
                        <span class="order-time">{{ getTimeElapsed(item.created_at) }}</span>
                      </div>
                    </div>
                    <div class="order-status">
                      <span class="status-badge" [class]="getStatusClass(item.status)">
                        <i class="fa" [class]="getStatusIcon(item.status)"></i>
                        {{ item.status === 'ordered' ? 'Ordered' : 'Accepted' }}
                      </span>
                    </div>
                  </div>
                </a>
              </div>

              <!-- Empty State -->
              <div class="empty-state" *ngIf="activeOrders?.length === 0">
                <div class="empty-content">
                  <i class="fa fa-clock empty-icon"></i>
                  <h4>No Active Orders</h4>
                  <p>All orders have been completed or there are no active orders at the moment.</p>
                </div>
              </div>
            </div>
          </ng-template>
        </li>

        <!-- Buzzes tab -->
        <li ngbNavItem>
          <button class="nav-button" ngbNavLink>
            <div class="nav-content">
              <i class="fa fa-check-circle nav-icon"></i>
              <span class="nav-label">Completed Orders</span>
            </div>
          </button>
          <ng-template ngbNavContent>
            <!-- Tab Loading State -->
            <div class="tab-loading" *ngIf="completedLoading">
              <div class="tab-loader">
                <div class="loader-bar"></div>
                <div class="loader-bar"></div>
                <div class="loader-bar"></div>
              </div>
              <p>Loading completed orders...</p>
            </div>

            <!-- Orders List -->
            <div class="orders-list" *ngIf="!completedLoading">
              <div class="order-card" *ngFor="let item of completedOrders; trackBy: trackByOrderId">
                <div class="order-header">
                  <div class="order-info">
                    <a [routerLink]="['/orders/', item.uid]" class="order-link">
                      <h6 class="order-title">
                        <i class="fa fa-utensils"></i>
                        Order from {{ item.barcode_details?.name }}
                      </h6>
                    </a>
                    <span class="order-time">{{ getTimeElapsed(item.created_at) }}</span>
                  </div>
                  <div class="order-actions">
                    <span class="status-badge status-completed">
                      <i class="fa fa-check-circle"></i>
                      Order Completed
                    </span>
                    <button class="btn btn-print" (click)="printOrder(item)" title="Print Order">
                      <i class="fa fa-print"></i>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Empty State -->
              <div class="empty-state" *ngIf="completedOrders?.length === 0">
                <div class="empty-content">
                  <i class="fa fa-check-circle empty-icon"></i>
                  <h4>No Completed Orders</h4>
                  <p>No orders have been completed yet.</p>
                </div>
              </div>
            </div>
          </ng-template>
        </li>

        <!-- Buzzes tab -->
        <li ngbNavItem>
          <button class="nav-button" ngbNavLink>
            <div class="nav-content">
              <i class="fa fa-bell nav-icon"></i>
              <span class="nav-label">Buzzes</span>
              <span class="badge badge-warning" *ngIf="activeBuzzes.length > 0">
                {{activeBuzzes.length}}
              </span>
            </div>
          </button>
          <ng-template ngbNavContent>
            <div class="orders-list">
              <div class="buzz-card" *ngFor="let buzz of activeBuzzes; let i = index">
                <div class="buzz-header">
                  <div class="buzz-info">
                    <h6 class="buzz-title">
                      <i class="fa fa-bell text-warning"></i>
                      {{buzz.table}} needs attention
                    </h6>
                    <span class="buzz-time">{{ getTimeElapsed(buzz.timestamp) }}</span>
                  </div>
                  <button class="btn btn-clear" (click)="clearBuzz(i)">
                    <i class="fa fa-times"></i>
                    Clear
                  </button>
                </div>
              </div>

              <!-- Empty State -->
              <div class="empty-state" *ngIf="activeBuzzes.length === 0">
                <div class="empty-content">
                  <i class="fa fa-bell-slash empty-icon"></i>
                  <h4>No Active Buzzes</h4>
                  <p>All tables are content. No buzzes at the moment.</p>
                </div>
              </div>
            </div>
          </ng-template>
        </li>
      </ul>

      <!-- Tab Content -->
      <div [ngbNavOutlet]="nav" class="tab-content"></div>
    </div>
  </div>
</div>


<!-- Create Order Modal -->
<ng-template let-modal #content>
  <div class="modern-modal">
    <!-- Modal Header -->
    <div class="modal-header">
      <div class="modal-title-section">
        <div class="modal-icon">
          <i class="fa fa-plus-circle"></i>
        </div>
        <div class="modal-title-text">
          <h3 class="modal-title">Create New Order</h3>
          <p class="modal-subtitle">Select a table to start creating an order</p>
        </div>
      </div>
      <button type="button" class="modal-close-btn" (click)="modal.dismiss('Cross click')">
        <i class="fa fa-times"></i>
      </button>
    </div>

    <!-- Modal Body -->
    <div class="modal-body">
      <form [formGroup]="orderForm" class="order-form">
        <div class="form-field">
          <label for="barcode_id" class="field-label">
            <i class="fa fa-qrcode"></i>
            Table Selection
          </label>
          <div class="select-wrapper">
            <ng-select
              [items]="barcodeList"
              [searchable]="true"
              bindLabel="name"
              bindValue="uid"
              placeholder="Search and select a table"
              formControlName="barcode_id"
              class="modern-select">
            </ng-select>
          </div>
          <small class="field-help">Choose the table where you want to create the order</small>
        </div>
      </form>
    </div>

    <!-- Modal Footer -->
    <div class="modal-footer">
      <button type="button" class="btn-secondary" (click)="modal.dismiss('cancel')">
        <i class="fa fa-times"></i>
        Cancel
      </button>
      <button class="btn-primary" (click)="start()" [disabled]="orderForm.invalid">
        <i class="fa fa-arrow-right"></i>
        Continue to Order
      </button>
    </div>
  </div>
</ng-template>
<!-- Accept Order Modal -->
<ng-template let-modal #content4>
  <div class="modern-modal">
    <!-- Modal Header -->
    <div class="modal-header">
      <div class="modal-title-section">
        <div class="modal-icon accept-icon">
          <i class="fa fa-clock"></i>
        </div>
        <div class="modal-title-text">
          <h3 class="modal-title">Set Wait Time</h3>
          <p class="modal-subtitle">How long should the customer wait for this order?</p>
        </div>
      </div>
      <button type="button" class="modal-close-btn" (click)="modal.dismiss('Cross click')">
        <i class="fa fa-times"></i>
      </button>
    </div>

    <!-- Modal Body -->
    <div class="modal-body">
      <div class="wait-time-section">
        <div class="time-selector">
          <button class="time-btn decrease-btn" (click)="minus()" type="button">
            <i class="fa fa-minus"></i>
          </button>

          <div class="time-display">
            <input type="number"
                   [(ngModel)]="minutes"
                   class="time-input"
                   min="1"
                   max="120">
            <span class="time-unit">minutes</span>
          </div>

          <button class="time-btn increase-btn" (click)="plus()" type="button">
            <i class="fa fa-plus"></i>
          </button>
        </div>

        <div class="time-presets">
          <button class="preset-btn" (click)="setMinutes(5)" [class.active]="minutes === 5">5 min</button>
          <button class="preset-btn" (click)="setMinutes(10)" [class.active]="minutes === 10">10 min</button>
          <button class="preset-btn" (click)="setMinutes(15)" [class.active]="minutes === 15">15 min</button>
          <button class="preset-btn" (click)="setMinutes(20)" [class.active]="minutes === 20">20 min</button>
        </div>

        <div class="estimated-time">
          <i class="fa fa-info-circle"></i>
          <span>Estimated completion: {{ getEstimatedTime() }}</span>
        </div>
      </div>
    </div>

    <!-- Modal Footer -->
    <div class="modal-footer">
      <button type="button" class="btn-secondary" (click)="modal.dismiss('cancel')">
        <i class="fa fa-times"></i>
        Cancel
      </button>
      <button class="btn-accept" (click)="Accept(tempItem)">
        <i class="fa fa-check"></i>
        Accept Order
      </button>
    </div>
  </div>
</ng-template>
